import useMutationApi from '../useMutationApi';

import type { WorkflowConstants } from 'src/pages/authenticated/core/spreadsheets/types/WorkflowConstants';
import type { OverlapResolution } from '@erp/shared-types';

interface SubmitTransformedCSVRequest {
  transformationType: string;
  constants: WorkflowConstants;
  transformedData: string;
  filename: string;
  sheetName?: string;
  originalFiles: File[];
  /**
   * How to handle overlapping invoices:
   * - 'override': Delete existing overlapping invoices before creating new ones
   * - 'keep_both': Create new data alongside existing data
   * - undefined: If overlap exists, server will return 409 Conflict
   */
  overlapResolution?: OverlapResolution;
}

interface SubmitTransformedCSVResponse {
  transformationType: string;
  filename: string;
  totalLineItems: number;
  agreementId: string;
}

const useSubmitTransformedCSV = (setBannerError?: (error: string) => void) => {
  return useMutationApi<
    SubmitTransformedCSVRequest,
    SubmitTransformedCSVResponse
  >({
    endpoint: 'spreadsheet/csv/submit',
    method: 'POST',
    successMessage: 'Data submitted successfully',
    transformRequest: (data: SubmitTransformedCSVRequest) => {
      // Convert to FormData to include original files
      const formData = new FormData();
      formData.append('transformationType', data.transformationType);
      formData.append('transformedData', data.transformedData);
      formData.append('filename', data.filename);

      if (data.sheetName) {
        formData.append('sheetName', data.sheetName);
      }

      // Add overlap resolution if provided
      if (data.overlapResolution) {
        formData.append('overlapResolution', data.overlapResolution);
      }

      // Add constants as flattened object fields
      Object.entries(data.constants).forEach(([key, value]) => {
        formData.append(`constants[${key}]`, String(value || ''));
      });

      // Add original files
      data.originalFiles.forEach((file) => {
        formData.append('files', file);
      });

      return formData;
    },
    errorHandler: (errorResponse: unknown) => {
      const err = errorResponse as {
        status?: number;
        message?: string;
        error?: string;
      };
      const message =
        err?.error || err?.message || 'Submission failed due to unknown error';

      // Show banner for 4xx validation/client errors
      if (err.message && setBannerError) {
        setBannerError(message);
      }

      const error = new Error(message);
      (error as Error & { response?: { data: unknown } }).response = {
        data: errorResponse,
      };
      return error;
    },
  });
};

export default useSubmitTransformedCSV;
